import Badge from '@/app/components/common/Badge'
import Text from '@/app/components/common/Text'
import Title from '@/app/components/common/Title'
import TranslateString from '@/app/components/common/TranslateString'
import Stars from '@/app/components/Stars'
import { Icons } from '@/app/components/ui/Icons'
import { SearchItem, SearchItemLocationType } from '@/app/types'
import { ratingsTranslations } from '@/app/utils'
import { camelCase, startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'

interface SiteHeadingProps {
	showNewHeading: boolean
	customHeading: boolean
	selectedItem: SearchItem | null
	sanityData?: {
		headingOne?: string
	}
	isDesktop: boolean
	isLandingPage?: boolean
	hotel?: any
	isMd: boolean
}

const HeadingLoader = () => <div className="min-h-[60px] w-full"></div>

const SiteHeading: React.FC<SiteHeadingProps> = ({
	showNewHeading,
	customHeading,
	selectedItem,
	sanityData,
	hotel,
	isDesktop,
	isLandingPage,
}) => {
	const tRating = useTranslations('guestRatings')
	const [defaultSelectedItem, setDefaultSelectedItem] =
		useState<SearchItem | null>(null)

	useEffect(() => {
		if (!defaultSelectedItem) {
			setDefaultSelectedItem(selectedItem)
		}
	}, [selectedItem])

	const headingContent = () => {
		if (isLandingPage && defaultSelectedItem) {
			if (defaultSelectedItem.loctype === SearchItemLocationType.HOTEL) {
				const formattedHotelName = startCase(
					camelCase(defaultSelectedItem.name)
				)
				return (
					<TranslateString
						context="Main"
						i18nKey="newDesign.mpHotelHeading"
						variables={{
							hotelName: formattedHotelName,
						}}
					/>
				)
			}
			return defaultSelectedItem.name
		}

		if (!showNewHeading) {
			return <HeadingLoader />
		}

		if (customHeading && !sanityData?.headingOne) {
			if (selectedItem?.cityonly) {
				return (
					<TranslateString
						context="Main"
						i18nKey="destCodePage.siteHeading"
						variables={{
							location: isLandingPage
								? defaultSelectedItem?.cityonly || ''
								: selectedItem.cityonly,
						}}
					/>
				)
			} else {
				return (
					<TranslateString context="Main" i18nKey="newDesign.siteHeading" />
				)
			}
		}

		return sanityData?.headingOne ? (
			<>{sanityData.headingOne}</>
		) : (
			<TranslateString context="Main" i18nKey="newDesign.siteHeading" />
		)
	}

	const Ratings = () => {
		if (hotel?.ratingText) {
			const mappedRating = tRating(ratingsTranslations(hotel.rating));

			return (
				<Text
					size="md"
					color={'white'}
					className="font-semibold max-lg:text-dark-blue max-xl:text-sm"
				>
					<span className="lg:hidden">{hotel?.rating || '0.0'}</span>{' '}
					{mappedRating}
				</Text>
			)
		}
		return <div className="opacity-30 w-11 h-3 rounded-xl bg-white" />
	}

	if (isLandingPage) {
		const locationContentString = defaultSelectedItem ?
			`${startCase(camelCase(defaultSelectedItem?.cityonly))}, ${startCase(camelCase(defaultSelectedItem?.country))}`
			: '';
		return (
			<div className="hotel-site-heading">
				{!hotel ? (
					<Stars
						count={5}
						fill="white"
						className="stars flex items-center opacity-30 justify-center"
					/>
				) : (
					<Stars
						count={hotel?.stars}
						fill="white"
						className="stars flex items-center justify-center"
					/>
				)}
				<Title
					className="site-heading my-2 !normal-case"
					level="1"

					testId="site-heading"
					id="site-heading"
				>
					{headingContent()}
				</Title>
				<div className="hotel-rating full-width max-xl:mb-6">
					<div className="hotel-list-item__location md:min-w-28 md:gap-2 gap-1">
						<Icons.iconPin stroke={isDesktop ? '#fff' : '#000'} />
						<div className="hotel-list-item__location-content">
							<Text
								size="md"
								color={'white'}
								className="capitalize max-xl:text-sm"
							>
								{locationContentString}
							</Text>
						</div>
					</div>
					<div className="flex row items-center md:min-w-28 gap-2">
						<span className="max-lg:hidden">
							<Badge
								bgColor={hotel?.rating ? 'white' : 'white/30'}
								textColor={hotel?.rating ? 'transparent' : 'white'}
							>
								{hotel?.rating || '0.0'}
							</Badge>
						</span>
						<Ratings />
					</div>
				</div>
			</div>
		)
	}

	return (
		<Title
			className="site-heading px-3"
			level="1"

			testId="site-heading"
			id="site-heading"
		>
			{headingContent()}
		</Title>
	)
}

export default SiteHeading
